import { inject, Injectable } from '@angular/core';
import { OverviewRecordDto } from '@myarvato/modules/transport/models';
import { DataExportService, HeaderDefinitionCollection } from '@myarvato/shared/export';
import * as ExcelJS from 'exceljs';
import { ColumnDefinitionBase } from '@myarvato/shared/table';
import { TranslateService } from '@ngx-translate/core';
import { TransportExportRetrievalService } from './transport-export-retrieval.service';
import { TableLazyLoadEvent } from 'primeng/table';
import { ExportProgressDialogComponent } from '../components/export-progress-dialog/export-progress-dialog.component';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { MyArvatoDialogService } from '@myarvato/shared/dialog';
import { ConfirmationConfig, ConfirmDialogService } from '@myarvato/components/confirm-dialog';

@Injectable({
  providedIn: 'root',
})
export class TrackingListExportService {
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly dataExportService = inject(DataExportService);
  private readonly dialogService = inject(MyArvatoDialogService);
  private readonly translateService = inject(TranslateService);
  private readonly transportExportRetrievalService = inject(TransportExportRetrievalService);

  public async exportCurrentPage(tableData: OverviewRecordDto[], columns: ColumnDefinitionBase[], type: 'csv' | 'xlsx'): Promise<void> {
    if (!(await this.notifyDataSecurity())) {
      return;
    }

    await this.createExcelSheet(tableData, columns, type, 'Transport Data');
  }

  public async exportAllPages(
    tableFilter: TableLazyLoadEvent | undefined,
    columns: ColumnDefinitionBase[],
    totalNumberOfRows: number,
    type: 'csv' | 'xlsx',
  ): Promise<void> {
    if (!(await this.notifyDataSecurity())) {
      return;
    }

    if (!tableFilter) {
      return;
    }

    const dialogRef = this.showExportProgressDialog();
    const rows = await this.transportExportRetrievalService.getExportData(tableFilter, totalNumberOfRows);

    if (this.transportExportRetrievalService.aborted()) {
      this.transportExportRetrievalService.aborted.set(false);
    } else {
      await this.createExcelSheet(rows, columns, type, 'Transport Data Complete');
    }

    dialogRef?.close();
  }

  private async createExcelSheet(
    rows: OverviewRecordDto[],
    columns: ColumnDefinitionBase[],
    type: 'csv' | 'xlsx',
    title: string,
  ): Promise<void> {
    const headerDefinition: HeaderDefinitionCollection = {};
    columns.forEach((column) => {
      headerDefinition[column.fieldsIdentifier] = { value: column.header, format: column.formatValue };
    });

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(title);
    worksheet.columns = columns.map((column) => ({
      header: column.header,
      key: column.fieldsIdentifier,
    }));

    worksheet.addRows(this.toExcelObjects(rows, columns));
    await this.dataExportService.saveFile(workbook, title, type);
  }

  private toExcelObjects(rows: OverviewRecordDto[], columns: ColumnDefinitionBase[]): unknown[] {
    return rows.map((item) => {
      const excelObject: { [key: string]: string } = {};
      columns.forEach((column) => {
        const value = item[column.fieldsIdentifier as keyof typeof item];
        excelObject[column.fieldsIdentifier] = column.formatValue ? column.formatValue(value) : value;
      });
      return excelObject;
    });
  }

  private showExportProgressDialog(): DynamicDialogRef | null {
    return this.dialogService.showDialog(ExportProgressDialogComponent, {
      showHeader: false,
    });
  }

  private async notifyDataSecurity(): Promise<boolean> {
    const config: ConfirmationConfig = {
      message: this.translateService.instant('download-dialog.question'),
      header: this.translateService.instant('download-dialog.header'),
      acceptButtonProps: {
        label: this.translateService.instant('download-dialog.yes'),
      },
      rejectButtonProps: {
        label: this.translateService.instant('general.cancel'),
      },
      size: 'lg',
      icon: 'pi pi-download',
    };

    return this.confirmDialogService.confirm(config);
  }
}
